import React from 'react';
import { useNavigate } from 'react-router';
import { Formik } from 'formik';

import { ValidationCreateNews } from 'common/validations';
import PrimaryButton from 'components/form/buttons/primaryButton';
import TextIput from 'components/form/textInput';
import Gap from 'components/gap';
import { useActions } from 'common/hooks/useActions';
import UploadBox from 'components/form/uploadBox';
import TextArea from 'components/form/texArea';

import * as S from '../../styles';
import * as T from 'common/types';

interface IProps {
  data?: T.Pages.NewsListItem;
  closeModal?: () => void;
  fetchData?: () => void;
  loading: boolean;
}

const NewsForm: React.FC<IProps> = ({ data, loading }) => {
  const nav = useNavigate();

  const { newsCreateRequestAction, newsUpdateRequestAction, newsUploadRequestAction, newsUpdateUploadRequestAction } =
    useActions();

  return (
    <>
      <Formik
        initialValues={{
          en_title: data?.newsTranslations?.find((t) => t.language === 'en')?.title || '',
          ar_title: data?.newsTranslations?.find((t) => t.language === 'ar')?.title || '',
          en_body: data?.newsTranslations?.find((t) => t.language === 'en')?.body || '',
          ar_body: data?.newsTranslations?.find((t) => t.language === 'ar')?.body || '',
          reference: data?.reference || '',
          image: data?.imageName || '',
          file: undefined,
          fileUrl: data?.image || ''
        }}
        enableReinitialize
        validationSchema={data?.id ? ValidationCreateNews : ValidationCreateNews}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          const newData = {
            reference: values.reference,
            translations: [
              {
                title: values.en_title,
                body: values.en_body,
                language: 'en'
              },
              {
                title: values.ar_title,
                body: values.ar_body,
                language: 'ar'
              }
            ]
          };
          if (data?.id)
            newsUpdateRequestAction({ ...newData, id: data?.id }, () => {
              resetForm();
              nav('/panel/news');
            });
          else
            newsCreateRequestAction({ ...newData, image: values.image }, () => {
              resetForm();
              nav('/panel/news');
            });
          setSubmitting(false);
        }}
      >
        {({ values, errors, touched, handleChange, handleSubmit, isSubmitting, setValues }) => (
          <S.Div className="row">
            <S.Div className="col-md-12">
              <Gap size="10px" />
              <div className="w-full d-flex align-items-center gap-4">
                <TextIput
                  type="text"
                  name="en_title"
                  onChange={handleChange}
                  placeholder="Title"
                  label="English Title"
                  value={values.en_title}
                  message={touched.en_title && (errors.en_title as any)}
                  error={touched.en_title && (errors.en_title as unknown as boolean)}
                  inputStyle={{ borderRadius: 7 }}
                />

                <TextIput
                  type="text"
                  name="ar_title"
                  onChange={handleChange}
                  placeholder="الرجاء إدخال العنوان"
                  label="Arabic Title"
                  value={values.ar_title}
                  message={touched.ar_title && (errors.ar_title as any)}
                  error={touched.ar_title && (errors.ar_title as unknown as boolean)}
                  inputStyle={{ borderRadius: 7 }}
                />
              </div>
              <Gap size="10px" />

              <div className="w-full d-flex align-items-center gap-4">
                <TextArea
                  label="English Description"
                  name="en_body"
                  onChange={handleChange}
                  placeholder="Description"
                  value={values.en_body}
                  message={touched.en_body && (errors.en_body as any)}
                  error={touched.en_body && (errors.en_body as unknown as boolean)}
                  inputStyle={{ borderRadius: 7 }}
                />

                <TextArea
                  label="Arabic Description"
                  name="ar_body"
                  onChange={handleChange}
                  placeholder="الرجاء إدخال الوصف"
                  value={values.ar_body}
                  message={touched.ar_body && (errors.ar_body as any)}
                  error={touched.ar_body && (errors.ar_body as unknown as boolean)}
                  inputStyle={{ borderRadius: 7 }}
                />
              </div>

              <Gap size="10px" />

              <div className="w-full d-flex align-items-start gap-4">
                <TextIput
                  type="text"
                  name="reference"
                  onChange={handleChange}
                  placeholder="Reference"
                  label="Reference"
                  value={values.reference}
                  message={touched.reference && (errors.reference as any)}
                  error={touched.reference && (errors.reference as unknown as boolean)}
                  inputStyle={{ borderRadius: 7 }}
                />

                <UploadBox
                  placeholder="Please Upload Image with 1.7 : 1 dimension"
                  label="Event Cover Photo"
                  inputStyle={{ height: 56, borderRadius: 7 }}
                  onChange={(e: T.Form.FileUpload) => {
                    if (data?.id)
                      newsUpdateUploadRequestAction({ image: e?.file, id: data?.id }, (fileNameOnly: string) => {
                        setValues({
                          ...values,
                          image: fileNameOnly,
                          fileUrl: e.dataURL,
                          file: e?.file as any
                        });
                      });
                    else
                      newsUploadRequestAction({ image: e?.file }, (fileNameOnly: string) => {
                        setValues({
                          ...values,
                          image: fileNameOnly,
                          fileUrl: e.dataURL,
                          file: e?.file as any
                        });
                      });
                  }}
                  message={touched.file && (errors.file as any)}
                  error={touched.file && (errors.file as any)}
                  key={data?.id}
                  defaultImage={values.fileUrl}
                  value={{ file: values.file, dataURL: values.fileUrl }}
                />
              </div>

              <Gap size="20px" />

              <PrimaryButton
                style={{
                  width: '216px',
                  height: 42,
                  borderRadius: 8
                }}
                onClick={handleSubmit}
                disabled={isSubmitting || loading}
              >
                {data?.id ? 'Edit News' : 'Create News'}
              </PrimaryButton>
            </S.Div>
          </S.Div>
        )}
      </Formik>
    </>
  );
};

export default NewsForm;
